import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ArrowLeft,
  CheckCircle2,
  FileImage,
  LoaderCircle,
  Save,
} from "lucide-react";
import { toast } from "sonner";

import LocationPickerMap from "@/components/LocationPickerMap";
import { useAuth } from "@/contexts/AuthContext";
import { useTerritoryCatalog } from "@/hooks/useTerritoryCatalog";
import { api, getApiErrorMessage } from "@/lib/api";
import { INITIATIVE_ACCESS_OPTIONS, POPULATION_FOCUS_OPTIONS } from "@/lib/polenica";
import {
  hasText,
  INITIATIVE_INITIAL,
  inputClass,
  isValidEmail,
  isValidPhone,
  textareaClass,
} from "@/features/auth/register-wizard/config";
import {
  Field,
  MultiSelectGrid,
  StatusChip,
  UploadCard,
} from "@/features/auth/register-wizard/primitives";
import { VisibilityToggles } from "@/features/auth/register-wizard/steps";

const SUCCESS_MESSAGE = "La iniciativa fue publicada correctamente en el mapa.";
const DEFAULT_COORDINATE = { latitud: 6.2442, longitud: -75.5812 };
const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function readDraft(key) {
  try {
    const stored = window.localStorage.getItem(key);
    if (!stored) {
      return null;
    }
    const draft = JSON.parse(stored);
    if (!draft?.savedAt || Date.now() - draft.savedAt > DRAFT_MAX_AGE_MS) {
      window.localStorage.removeItem(key);
      return null;
    }
    return draft;
  } catch (_error) {
    window.localStorage.removeItem(key);
    return null;
  }
}

const ACCESS_LABELS = {
  ABIERTO: "Abierta al público",
  RESTRINGIDO: "Con visita acordada",
  PRIVADO: "Privada",
};

function renderSelectOptions(options) {
  return options.map((option) => {
    if (typeof option === "string") {
      return (
        <option key={option} value={option}>
          {option}
        </option>
      );
    }

    return (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    );
  });
}

function normalizeTerritorySelection(municipio, comuna, barrio) {
  if (municipio !== "Medellin") {
    return {
      comuna: comuna || "No aplica",
      barrio: barrio || "No aplica",
    };
  }

  return { comuna, barrio };
}

function normalizeAccess(value) {
  const normalized = String(value || "").trim().toUpperCase();
  if (["ABIERTO", "RESTRINGIDO", "PRIVADO"].includes(normalized)) {
    return normalized;
  }
  if (normalized.includes("ABIERT")) {
    return "ABIERTO";
  }
  if (normalized.includes("PRIVAD")) {
    return "PRIVADO";
  }
  return "RESTRINGIDO";
}

function buildInitiativeType(tipoIniciativa, tipoAcceso) {
  if (tipoIniciativa === "PACA_DIGESTORA") {
    return tipoAcceso === "ABIERTO" ? "paca_comunitaria" : "paca_privada";
  }

  return tipoAcceso === "ABIERTO" ? "huerta_comunitaria" : "huerta_privada";
}

function toNumber(value, fallback) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

async function uploadSingleFile(file) {
  const formData = new FormData();
  formData.append("file", file);

  const response = await api.post("/uploads/public", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
}

function hydrateInitiativeForm(data) {
  const visibility = data?.visibilidad_contacto || {};

  return {
    ...INITIATIVE_INITIAL,
    nombre: data?.nombre || "",
    tipo_iniciativa: data?.tipo_iniciativa || "HUERTA",
    tipo_acceso: normalizeAccess(data?.tipo_acceso_enum || data?.tipo_acceso),
    descripcion: data?.descripcion || "",
    direccion: data?.direccion || "",
    latitud: toNumber(data?.latitud, DEFAULT_COORDINATE.latitud),
    longitud: toNumber(data?.longitud, DEFAULT_COORDINATE.longitud),
    pais: data?.pais || "Colombia",
    region: data?.region || "Antioquia",
    ciudad: data?.ciudad || data?.municipio || "Medellin",
    municipio: data?.municipio || "Medellin",
    comuna: data?.comuna || "",
    barrio: data?.barrio || "",
    nombre_contacto: data?.nombre_contacto || "",
    celular_contacto: data?.celular_contacto || "",
    email_contacto: data?.email_contacto || "",
    sitio_web: data?.sitio_web || "",
    instagram_url: data?.instagram_url || "",
    facebook_url: data?.facebook_url || "",
    whatsapp_url: data?.whatsapp_url || "",
    publicar_nombre_contacto: Boolean(visibility.publicar_nombre_contacto),
    publicar_celular_contacto: Boolean(visibility.publicar_celular_contacto),
    publicar_email_contacto: Boolean(visibility.publicar_email_contacto),
    publicar_direccion: Boolean(visibility.publicar_direccion),
    publicar_horarios: Boolean(visibility.publicar_horarios),
    publicar_whatsapp: Boolean(visibility.publicar_whatsapp),
    publicar_enfoque_poblacional: Boolean(visibility.publicar_enfoque_poblacional),
    horarios_encuentro: data?.horarios_encuentro || "",
    ano_inicio: data?.ano_inicio ? String(data.ano_inicio) : "",
    enfoque_poblacional: Array.isArray(data?.enfoque_poblacional) ? data.enfoque_poblacional : [],
    asset_public_id: data?.asset_public_id || "",
  };
}

export default function RegisterInitiative() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const isEditing = Boolean(editId);
  const draftKey = `polenica:initiative-draft:${user?.id || "user"}:${editId || "new"}`;
  const {
    loading: territoryLoading,
    municipalities,
    getComunasForMunicipio,
    getBarriosForComuna,
  } = useTerritoryCatalog();

  const [form, setForm] = useState(INITIATIVE_INITIAL);
  const [initiativeFile, setInitiativeFile] = useState(null);
  const [initiativeUpload, setInitiativeUpload] = useState(null);
  const [localPreviewUrl, setLocalPreviewUrl] = useState("");
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [loadingDetail, setLoadingDetail] = useState(Boolean(editId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [draftReady, setDraftReady] = useState(false);
  const [draftRestored, setDraftRestored] = useState(false);

  const usesMedellin = form.municipio === "Medellin";
  const comunas = getComunasForMunicipio(form.municipio);
  const barrios = getBarriosForComuna(form.municipio, form.comuna);
  const hasMunicipalityBarrios = !usesMedellin && barrios.length > 0;
  const imagePreview = localPreviewUrl || initiativeUpload?.secure_url || existingImageUrl;

  useEffect(() => {
    if (isEditing || !user) {
      return;
    }

    const fullName = `${user.nombres || ""} ${user.apellidos || ""}`.trim();
    setForm((current) => ({
      ...current,
      nombre_contacto: current.nombre_contacto || fullName,
      celular_contacto: current.celular_contacto || user.celular || "",
      email_contacto: current.email_contacto || user.email || "",
      municipio: current.municipio || user.municipio || "Medellin",
      ciudad: current.ciudad || user.municipio || "Medellin",
      comuna: current.comuna || user.comuna || "",
      barrio: current.barrio || user.barrio || "",
    }));
  }, [isEditing, user]);

  useEffect(() => {
    if (isEditing || !user) {
      return;
    }

    const draft = readDraft(draftKey);
    if (draft?.form) {
      setForm({ ...INITIATIVE_INITIAL, ...draft.form });
      setInitiativeUpload(draft.initiativeUpload || null);
      setExistingImageUrl(draft.existingImageUrl || "");
      setDraftRestored(true);
    }
    setDraftReady(true);
  }, [draftKey, isEditing, user]);

  useEffect(() => {
    if (!editId) {
      setLoadingDetail(false);
      return;
    }

    let active = true;
    setLoadingDetail(true);
    setError("");

    api
      .get(`/initiatives/${editId}`)
      .then((response) => {
        if (!active) {
          return;
        }
        const data = response.data || {};
        const hydrated = hydrateInitiativeForm(data);
        const draft = readDraft(draftKey);
        setForm(draft?.form ? { ...hydrated, ...draft.form } : hydrated);
        setInitiativeUpload(draft?.initiativeUpload || null);
        setExistingImageUrl(draft?.existingImageUrl || data.image_url || data.fotos?.[0] || "");
        setDraftRestored(Boolean(draft?.form));
      })
      .catch((loadError) => {
        if (!active) {
          return;
        }
        setError(loadError.response?.data?.detail || "No fue posible cargar la iniciativa para edición.");
      })
      .finally(() => {
        if (active) {
          setLoadingDetail(false);
          setDraftReady(true);
        }
      });

    return () => {
      active = false;
    };
  }, [draftKey, editId]);

  useEffect(() => {
    if (!draftReady || successMessage) {
      return;
    }
    window.localStorage.setItem(
      draftKey,
      JSON.stringify({
        savedAt: Date.now(),
        form,
        initiativeUpload,
        existingImageUrl,
      }),
    );
  }, [draftKey, draftReady, existingImageUrl, form, initiativeUpload, successMessage]);

  useEffect(() => {
    if (!initiativeFile) {
      setLocalPreviewUrl("");
      return undefined;
    }

    const nextUrl = URL.createObjectURL(initiativeFile);
    setLocalPreviewUrl(nextUrl);

    return () => {
      URL.revokeObjectURL(nextUrl);
    };
  }, [initiativeFile]);

  const validationErrors = useMemo(() => {
    const errors = {};
    const territory = normalizeTerritorySelection(form.municipio, form.comuna, form.barrio);

    if (!hasText(form.nombre, 3)) {
      errors.nombre = "Use al menos 3 caracteres.";
    }
    if (!hasText(form.descripcion, 10)) {
      errors.descripcion = "Describa la iniciativa con al menos 10 caracteres.";
    }
    if (!hasText(form.direccion, 5)) {
      errors.direccion = "La dirección es obligatoria.";
    }
    if (!hasText(form.municipio, 2)) {
      errors.municipio = "Municipio obligatorio.";
    }
    if (!hasText(territory.comuna, 2)) {
      errors.comuna = "Comuna o corregimiento obligatorio.";
    }
    if (!hasText(territory.barrio, 2)) {
      errors.barrio = "Barrio obligatorio.";
    }
    if (!hasText(form.nombre_contacto, 3)) {
      errors.nombre_contacto = "Ingrese un nombre de contacto válido.";
    }
    if (!isValidPhone(form.celular_contacto)) {
      errors.celular_contacto = "Ingrese un celular válido.";
    }
    if (!isValidEmail(form.email_contacto)) {
      errors.email_contacto = "Ingrese un correo válido.";
    }
    if (form.ano_inicio && (Number(form.ano_inicio) < 1900 || Number(form.ano_inicio) > 2100)) {
      errors.ano_inicio = "Ingrese un año entre 1900 y 2100.";
    }
    if (!form.enfoque_poblacional.length) {
      errors.enfoque_poblacional = "Seleccione al menos un enfoque poblacional.";
    }
    if (!initiativeFile && !initiativeUpload && !existingImageUrl) {
      errors.image = "Debe cargar una imagen principal.";
    }

    return errors;
  }, [existingImageUrl, form, initiativeFile, initiativeUpload]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleMunicipalityChange = (municipio) => {
    const nextBarrios = getBarriosForComuna(municipio, "");
    setForm((current) => ({
      ...current,
      ciudad: municipio,
      municipio,
      comuna: municipio === "Medellin" ? "" : "No aplica",
      barrio: municipio === "Medellin" || nextBarrios.length ? "" : "No aplica",
    }));
  };

  const ensureImageUploaded = async () => {
    if (initiativeUpload) {
      return initiativeUpload;
    }
    if (!initiativeFile) {
      return null;
    }

    const uploaded = await uploadSingleFile(initiativeFile);
    setInitiativeUpload(uploaded);
    return uploaded;
  };

  const buildPayload = (uploadedImage) => {
    const territory = normalizeTerritorySelection(form.municipio, form.comuna, form.barrio);
    const imageUrl = uploadedImage?.secure_url || existingImageUrl || null;
    const tipoAcceso = normalizeAccess(form.tipo_acceso);

    return {
      nombre: form.nombre,
      tipo: buildInitiativeType(form.tipo_iniciativa, tipoAcceso),
      tipo_iniciativa: form.tipo_iniciativa,
      descripcion: form.descripcion,
      direccion: form.direccion,
      tipo_acceso: ACCESS_LABELS[tipoAcceso] || ACCESS_LABELS.RESTRINGIDO,
      tipo_acceso_enum: tipoAcceso === "PRIVADO" ? "RESTRINGIDO" : tipoAcceso,
      horarios_encuentro: form.horarios_encuentro || null,
      ano_inicio: form.ano_inicio ? Number(form.ano_inicio) : null,
      latitud: Number(form.latitud),
      longitud: Number(form.longitud),
      pais: "Colombia",
      region: "Antioquia",
      ciudad: form.municipio,
      municipio: form.municipio,
      comuna: territory.comuna,
      barrio: territory.barrio,
      nombre_contacto: form.nombre_contacto,
      celular_contacto: form.celular_contacto,
      email_contacto: form.email_contacto,
      sitio_web: form.sitio_web || null,
      instagram_url: form.instagram_url || null,
      facebook_url: form.facebook_url || null,
      whatsapp_url: form.whatsapp_url || null,
      enfoque_poblacional: form.enfoque_poblacional,
      fotos: imageUrl ? [imageUrl] : [],
      asset_public_id: uploadedImage?.asset_public_id || form.asset_public_id || null,
      publicar_nombre_contacto: form.publicar_nombre_contacto,
      publicar_celular_contacto: form.publicar_celular_contacto,
      publicar_email_contacto: form.publicar_email_contacto,
      publicar_direccion: form.publicar_direccion,
      publicar_horarios: form.publicar_horarios,
      publicar_whatsapp: form.publicar_whatsapp,
      publicar_enfoque_poblacional: form.publicar_enfoque_poblacional,
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setAttempted(true);
    setError("");
    setSuccessMessage("");

    if (Object.keys(validationErrors).length || territoryLoading) {
      setError("Revise los campos marcados antes de enviar la iniciativa.");
      return;
    }

    setSaving(true);
    try {
      const uploadedImage = await ensureImageUploaded();
      const payload = buildPayload(uploadedImage);
      const response = isEditing
        ? await api.put(`/initiatives/${editId}`, payload)
        : await api.post("/initiatives", payload);

      const nextMessage = response.data?.message || SUCCESS_MESSAGE;
      setSuccessMessage(nextMessage);
      window.localStorage.removeItem(draftKey);
      toast.success(nextMessage);
      if (!isEditing) {
        setInitiativeFile(null);
      }
    } catch (submitError) {
      setError(getApiErrorMessage(submitError, "No fue posible guardar la iniciativa."));
    } finally {
      setSaving(false);
    }
  };

  if (loadingDetail) {
    return (
      <div className="flex min-h-[calc(100vh-180px)] items-center justify-center px-4 py-12">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-primary/20 border-t-primary" />
          <p className="mt-4 text-sm text-mutedForeground">Cargando iniciativa...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background px-4 py-8 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-6xl">
        <div className="mb-8 flex flex-col gap-4 rounded-[32px] border border-primary/15 bg-[linear-gradient(135deg,#DCECCF_0%,#F5F7EC_48%,#B9D3A4_100%)] p-6 shadow-[0_24px_70px_rgba(34,64,42,0.12)] md:flex-row md:items-center md:justify-between">
          <div>
            <button
              type="button"
              onClick={() => navigate(isEditing ? "/perfil" : "/mapa")}
              className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-white/75 px-4 py-2 text-sm font-semibold text-primary transition hover:bg-white"
            >
              <ArrowLeft className="h-4 w-4" />
              {isEditing ? "Volver al perfil" : "Volver al mapa"}
            </button>
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-primary/70">
              Mapa territorial
            </p>
            <h1 className="mt-2 font-heading text-3xl font-bold text-primary sm:text-4xl">
              {isEditing ? "Editar iniciativa del mapa" : "Registrar iniciativa en el mapa"}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-textMain">
              Complete la información de la huerta o paca urbana, defina qué datos serán públicos y ubique el punto
              aproximado antes de publicarla.
            </p>
          </div>
          <StatusChip tone="positive">Publicación inmediata</StatusChip>
        </div>

        <div className="rounded-[32px] border border-primary/15 bg-white p-6 shadow-[0_18px_55px_rgba(59,90,63,0.08)] md:p-8">
          {successMessage ? (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-[#7D9D54]/35 bg-[#EFF7E7] p-4">
              <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
              <p className="text-sm font-medium leading-6 text-primary">{successMessage}</p>
            </div>
          ) : null}

          {error ? (
            <div className="mb-6 flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4">
              <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600" />
              <div className="text-sm text-red-800">
                <p>{error}</p>
                {error.includes("sesión") ? (
                  <button
                    type="button"
                    onClick={() =>
                      navigate(
                        `/login?next=${encodeURIComponent(
                          `/registrar-iniciativa${editId ? `?edit=${editId}` : ""}`,
                        )}`,
                      )
                    }
                    className="mt-2 font-semibold underline underline-offset-2"
                  >
                    Renovar sesión
                  </button>
                ) : null}
              </div>
            </div>
          ) : null}

          {draftRestored && !successMessage ? (
            <div className="mb-6 rounded-2xl border border-primary/15 bg-primary/5 px-4 py-3 text-sm text-primary">
              Recuperamos el borrador guardado en este dispositivo.
            </div>
          ) : null}

          <form onSubmit={handleSubmit} className="grid gap-5 md:grid-cols-2">
            <Field label="Nombre de la iniciativa" required error={attempted ? validationErrors.nombre : ""}>
              <input
                value={form.nombre}
                onChange={(event) => updateForm("nombre", event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.nombre))}
              />
            </Field>

            <Field label="Tipo de iniciativa" required>
              <select
                value={form.tipo_iniciativa}
                onChange={(event) => updateForm("tipo_iniciativa", event.target.value)}
                className={inputClass(false)}
              >
                <option value="HUERTA">Huerta</option>
                <option value="PACA_DIGESTORA">Paca urbana</option>
                <option value="HUERTA_PACA_URBANA">Huerta y paca urbana</option>
              </select>
            </Field>

            <Field label="Tipo de acceso" required>
              <select
                value={form.tipo_acceso}
                onChange={(event) => updateForm("tipo_acceso", normalizeAccess(event.target.value))}
                className={inputClass(false)}
              >
                {INITIATIVE_ACCESS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Dirección" required error={attempted ? validationErrors.direccion : ""}>
              <input
                value={form.direccion}
                onChange={(event) => updateForm("direccion", event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.direccion))}
              />
            </Field>

            <Field label="Municipio" required error={attempted ? validationErrors.municipio : ""}>
              <select
                value={form.municipio}
                onChange={(event) => handleMunicipalityChange(event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.municipio))}
              >
                {renderSelectOptions(municipalities)}
              </select>
            </Field>

            <Field label="Comuna o corregimiento" required error={attempted ? validationErrors.comuna : ""}>
              <select
                value={form.comuna}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    comuna: event.target.value,
                    barrio: "",
                  }))
                }
                className={inputClass(attempted && Boolean(validationErrors.comuna))}
                disabled={!usesMedellin}
              >
                <option value="">{usesMedellin ? "Seleccione una comuna" : "No aplica"}</option>
                {usesMedellin ? renderSelectOptions(comunas) : null}
              </select>
            </Field>

            <Field label="Barrio" required error={attempted ? validationErrors.barrio : ""}>
              <select
                value={form.barrio}
                onChange={(event) => updateForm("barrio", event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.barrio))}
                disabled={!usesMedellin && !hasMunicipalityBarrios}
              >
                <option value="">{usesMedellin || hasMunicipalityBarrios ? "Seleccione un barrio" : "No aplica"}</option>
                {usesMedellin || hasMunicipalityBarrios ? renderSelectOptions(barrios) : null}
              </select>
            </Field>

            <Field label="Año de inicio" error={attempted ? validationErrors.ano_inicio : ""}>
              <input
                type="number"
                value={form.ano_inicio}
                onChange={(event) => updateForm("ano_inicio", event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.ano_inicio))}
                min="1900"
                max="2100"
              />
            </Field>

            <Field label="Nombre de contacto" required error={attempted ? validationErrors.nombre_contacto : ""}>
              <input
                value={form.nombre_contacto}
                onChange={(event) => updateForm("nombre_contacto", event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.nombre_contacto))}
              />
            </Field>

            <Field label="Celular de contacto" required error={attempted ? validationErrors.celular_contacto : ""}>
              <input
                value={form.celular_contacto}
                onChange={(event) => updateForm("celular_contacto", event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.celular_contacto))}
              />
            </Field>

            <Field label="Correo de contacto" required error={attempted ? validationErrors.email_contacto : ""}>
              <input
                type="email"
                value={form.email_contacto}
                onChange={(event) => updateForm("email_contacto", event.target.value)}
                className={inputClass(attempted && Boolean(validationErrors.email_contacto))}
              />
            </Field>

            <Field label="Horarios de atención">
              <input
                value={form.horarios_encuentro}
                onChange={(event) => updateForm("horarios_encuentro", event.target.value)}
                className={inputClass(false)}
                placeholder="Ej. Sábados de 8:00 a.m. a 12:00 m."
              />
            </Field>

            <Field label="Sitio web">
              <input
                value={form.sitio_web}
                onChange={(event) => updateForm("sitio_web", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="Instagram">
              <input
                value={form.instagram_url}
                onChange={(event) => updateForm("instagram_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="Facebook">
              <input
                value={form.facebook_url}
                onChange={(event) => updateForm("facebook_url", event.target.value)}
                className={inputClass(false)}
              />
            </Field>

            <Field label="WhatsApp">
              <input
                value={form.whatsapp_url}
                onChange={(event) => updateForm("whatsapp_url", event.target.value)}
                className={inputClass(false)}
                placeholder="Ej. 3001234567"
              />
            </Field>

            <div className="md:col-span-2">
              <VisibilityToggles
                value={form}
                onChange={updateForm}
                includeHours
                includePopulationFocus
              />
            </div>

            <div className="md:col-span-2">
              <Field label="Descripción" required error={attempted ? validationErrors.descripcion : ""}>
                <textarea
                  rows={4}
                  value={form.descripcion}
                  onChange={(event) => updateForm("descripcion", event.target.value)}
                  className={textareaClass(attempted && Boolean(validationErrors.descripcion))}
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field
                label="Enfoque poblacional"
                hint="Puede elegir más de una opción."
                error={attempted ? validationErrors.enfoque_poblacional : ""}
              >
                <MultiSelectGrid
                  options={POPULATION_FOCUS_OPTIONS}
                  value={form.enfoque_poblacional}
                  onToggle={(item) =>
                    setForm((current) => ({
                      ...current,
                      enfoque_poblacional: current.enfoque_poblacional.includes(item)
                        ? current.enfoque_poblacional.filter((currentItem) => currentItem !== item)
                        : [...current.enfoque_poblacional, item],
                    }))
                  }
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <Field
                label="Ubicación aproximada en el mapa"
                hint={`Latitud ${Number(form.latitud).toFixed(6)} / Longitud ${Number(form.longitud).toFixed(6)}`}
              >
                <LocationPickerMap
                  value={{ lat: form.latitud, lng: form.longitud }}
                  searchContext={{
                    direccion: form.direccion,
                    barrio: form.barrio,
                    comuna: form.comuna,
                    municipio: form.municipio,
                  }}
                  onChange={(coordinate) =>
                    setForm((current) => ({
                      ...current,
                      latitud: coordinate.lat,
                      longitud: coordinate.lng,
                    }))
                  }
                  helpText="El mapa se acerca usando dirección, barrio o comuna. También puede tocar el mapa para mover el punto."
                />
              </Field>
            </div>

            <div className="md:col-span-2">
              <UploadCard
                title={
                  initiativeFile
                    ? initiativeFile.name
                    : existingImageUrl || initiativeUpload
                      ? "Imagen principal cargada"
                      : "Cargar imagen principal"
                }
                subtitle="Agregue una imagen de referencia para identificar la iniciativa."
                icon={FileImage}
                onChange={(event) => {
                  setInitiativeFile(event.target.files?.[0] || null);
                  setInitiativeUpload(null);
                }}
              />
              {attempted && validationErrors.image ? (
                <p className="mt-2 text-xs font-medium text-[#8A3D22]">{validationErrors.image}</p>
              ) : null}
              {imagePreview ? (
                <div className="mt-3 overflow-hidden rounded-[20px] border border-border bg-white">
                  <img src={imagePreview} alt="Vista previa iniciativa" className="h-56 w-full object-cover" />
                </div>
              ) : null}
            </div>

            <div className="flex flex-col gap-3 border-t border-border pt-6 md:col-span-2 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm leading-6 text-mutedForeground">
                Al guardar, la iniciativa quedará publicada inmediatamente en el mapa.
              </p>
              <button
                type="submit"
                disabled={saving || territoryLoading}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3 text-sm font-semibold text-primaryForeground shadow-[0_16px_28px_rgba(59,90,63,0.18)] transition hover:bg-[#2C442F] disabled:cursor-not-allowed disabled:opacity-65"
              >
                {saving ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {isEditing ? "Guardar cambios" : "Publicar iniciativa"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
